import React from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { styled, keyframes } from "@mui/material/styles";
import { motion } from "framer-motion";
import {
  RocketLaunch,
  Restaurant,
  CheckCircle,
  ArrowForward,
  WhatsApp,
} from "@mui/icons-material";

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0.6; }
`;

const RotatingElement = styled(Box)(({ duration = 20 }) => ({
  animation: `${rotate} ${duration}s linear infinite`,
}));

const FloatingElement = styled(Box)({
  animation: `${float} 3s ease-in-out infinite`,
});

const PulseElement = styled(Box)({
  animation: `${pulse} 2s ease-in-out infinite`,
});

const FinalCTA = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Orange theme colors
  const ORANGE_PRIMARY = "var(--primary)";
  const ORANGE_DARK = "var(--primary-dark)";
  const ORANGE_LIGHT = "var(--primary-light)";

  const benefits = [
    "30-Day Free Trial",
    "No Setup Fee",
    "24/7 Support",
    "Cancel Anytime",
  ];

  return (
    <Box
      sx={{
        // py: { xs: 4, sm: 6, md: 10, lg: 12 },
        bgcolor: "#ffffff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Background */}
      <Box
        sx={{
          position: "absolute",
          top: -300,
          right: -300,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,107,0,0.04) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -300,
          left: -300,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,107,0,0.04) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Box >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Paper
            sx={{
              p: { xs: 3, sm: 4, md: 6, lg: 8 },
              // borderRadius: { xs: "20px", md: "30px" },
              background: `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE_PRIMARY} 50%, ${ORANGE_LIGHT} 100%)`,
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              boxShadow: "var(--shadow-orange-hover)",
            }}
          >
            {/* Animated Background Elements */}
            <RotatingElement duration={60}>
              <Box
                sx={{
                  position: "absolute",
                  top: -200,
                  right: -200,
                  width: 400,
                  height: 400,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.05)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -200,
                  left: -200,
                  width: 400,
                  height: 400,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.05)",
                }}
              />
            </RotatingElement>

            {/* Floating Decorations */}
            <FloatingElement>
              <Box
                sx={{
                  position: "absolute",
                  top: 50,
                  left: 50,
                  fontSize: 40,
                  opacity: 0.15,
                  display: { xs: "none", md: "block" },
                }}
              >
                <Restaurant sx={{ fontSize: 60, color: "#fff" }} />
              </Box>
            </FloatingElement>

            <FloatingElement>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 50,
                  right: 50,
                  fontSize: 40,
                  opacity: 0.15,
                  display: { xs: "none", md: "block" },
                }}
              >
                <Restaurant sx={{ fontSize: 60, color: "#fff" }} />
              </Box>
            </FloatingElement>

            <Box sx={{ position: "relative", zIndex: 2 }}>
              {/* Badge */}
              <Chip
                // icon={<RocketLaunch sx={{ fontSize: 16 }} />}
                label="GET STARTED TODAY"
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: { xs: "10px", md: "12px" },
                  letterSpacing: "2px",
                  px: 2,
                  py: 0.5,
                  mb: 3,
                  borderRadius: "8px",
                  backdropFilter: "blur(10px)",
                  "& .MuiChip-icon": { color: "#fff" },
                }}
              />

              {/* Animated Rocket Icon */}
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <Box
                  sx={{
                    width: { xs: 80, md: 100 },
                    height: { xs: 80, md: 100 },
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
                  }}
                >
                  <RocketLaunch
                    sx={{
                      fontSize: { xs: 40, md: 50 },
                      color: "#fff",
                    }}
                  />
                </Box>
              </motion.div>

              {/* Heading */}
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3.5rem",  },
                  fontWeight: 900,
                  color: "#fff",
                  mb: 2,
                  lineHeight: 1.2,
                }}
              >
                Ready to Transform Your{" "}
                <Box 
                  component="span" 
                  sx={{ 
                    color: "#fff", 
                    textDecoration: "none", 
                    textUnderlineOffset: 8,
                    textDecorationColor: "rgba(255,255,255,0.5)",
                  }}
                >
                  Restaurant
                </Box>
                ?
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  mb: 3,
                  maxWidth: "700px",
                  mx: "auto",
                  fontSize: { xs: "15px", sm: "16px", md: "18px" },
                  lineHeight: 1.7,
                }}
              >
                Start your 30-day free trial today. No credit card required!
              </Typography>

              {/* Benefits Chips */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: { xs: 1, md: 1.5 },
                  mb: 4,
                }}
              >
                {benefits?.map((benefit, index) => (
                  <Chip
                    key={index}
                    icon={<CheckCircle sx={{ fontSize: 14 }} />}
                    label={benefit}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.12)",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: { xs: "11px", md: "12px" },
                      px: 1,
                      py: 1.5,
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.15)",
                      "& .MuiChip-icon": { color: "#fff" },
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.2)",
                        transform: "translateY(-2px)",
                        transition: "all 0.3s",
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Buttons */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 4 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/register"
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: "#fff",
                    color: ORANGE_DARK,
                    px: { xs: 4, md: 6 },
                    py: { xs: 1.2, md: 1.5 },
                    borderRadius: "50px",
                    fontSize: { xs: "0.9rem", md: "1.1rem" },
                    fontWeight: 700,
                    minWidth: { xs: "100%", sm: 200 },
                    boxShadow: "0 18px 40px rgba(0,0,0,0.15)",
                    "&:hover": {
                      bgcolor: "#f0f0f0",
                      transform: "scale(1.05)",
                      boxShadow: "0 25px 50px rgba(0,0,0,0.2)",
                    },
                    transition: "all 0.3s",
                  }}
                >
                  Register Now
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  // component={Link}
                  // to="/contact"
                  startIcon={<WhatsApp />}
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.5)",
                    px: { xs: 4, md: 6 },
                    py: { xs: 1.2, md: 1.5 },
                    borderRadius: "50px",
                    fontSize: { xs: "0.9rem", md: "1.1rem" },
                    fontWeight: 700,
                    borderWidth: 2,
                    minWidth: { xs: "100%", sm: 200 },
                    "&:hover": {
                      borderColor: "#fff",
                      borderWidth: 2,
                      bgcolor: "rgba(255,255,255,0.1)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.3s",
                  }}
                   href="https://wa.me/916378788756?text=Hello RestaurantWala%20Team%2C%20I%20would%20like%20to%20inquire%20about%20your%20services."
                    target="_blank"
                >
                  WhatsApp
                </Button>
              </Stack>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </Box>
  );
};

export default FinalCTA;